import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import content from "app-content";
import styles from "./index.css";
import Icon from "../Icon";
import getMyAccountUrl from "../../../../services/myAccount/getMyAccountUrl";

const FADE_DURATION = 300;

const useBannerFade = isSuccess => {
  const [isFaded, setIsFaded] = useState(false);
  const [displayedIsSuccess, setDisplayedIsSuccess] = useState(false);

  useEffect(() => {
    if (isSuccess) {
      setIsFaded(true);
      const timeout = setTimeout(() => {
        setDisplayedIsSuccess(true);
        setIsFaded(false);
      }, FADE_DURATION);

      return () => clearTimeout(timeout);
    }
  }, [isSuccess]);

  return { isFaded, displayedIsSuccess };
};

const BannerIcon = ({ displayedIsSuccess }) => (
  <div className={styles.iconWrapper} aria-hidden="true">
    <Icon
      icon={displayedIsSuccess ? "check" : "notification-bell"}
      className={styles.icon}
    />
  </div>
);

BannerIcon.propTypes = {
  displayedIsSuccess: PropTypes.bool.isRequired
};

const BannerCopy = ({ displayedIsSuccess, onNotifyMe }) => (
  <div className={styles.content}>
    <p className={styles.title}>
      {displayedIsSuccess
        ? content.saved_items_stock_marketing_opt_in_prompt_confirmation_title
        : content.saved_items_stock_marketing_opt_in_prompt_title}
    </p>
    <div className={styles.descriptionWrapper}>
      {displayedIsSuccess ? (
        <>
          <p className={styles.description}>
            {
              content.saved_items_stock_marketing_opt_in_prompt_confirmation_body
            }
          </p>
          <a
            href={getMyAccountUrl("/my-account/contact-preferences")}
            className={styles.preferencesLink}
            target="_blank"
            rel="noopener noreferrer"
          >
            {
              content.saved_items_stock_marketing_opt_in_prompt_confirmation_body_hyperlink
            }
          </a>
        </>
      ) : (
        <>
          <p className={classnames(styles.description, styles.hideOnMobile)}>
            {content.saved_items_stock_marketing_opt_in_prompt_body_long}
          </p>
          <p className={classnames(styles.description, styles.hideOnDesktop)}>
            {content.saved_items_stock_marketing_opt_in_prompt_body_short}
          </p>
          <button
            type="button"
            className={classnames(
              styles.notifyMeButtonLink,
              styles.hideOnDesktop
            )}
            onClick={onNotifyMe}
          >
            {content.saved_items_stock_marketing_opt_in_prompt_button}
          </button>
        </>
      )}
    </div>
  </div>
);

BannerCopy.propTypes = {
  displayedIsSuccess: PropTypes.bool.isRequired,
  onNotifyMe: PropTypes.func.isRequired
};

const NotificationsOptInBanner = ({
  render,
  className,
  onNotifyMe,
  isSuccess
}) => {
  const bannerRef = useRef(null);
  const { isFaded, displayedIsSuccess } = useBannerFade(isSuccess);

  useEffect(() => {
    if (displayedIsSuccess && bannerRef.current) {
      bannerRef.current.focus();
    }
  }, [displayedIsSuccess]);

  return (
    <div
      ref={bannerRef}
      tabIndex="-1"
      className={classnames(className, styles.bannerContainer, {
        [styles.bannerFaded]: isFaded,
        [styles.preOptInBanner]: !displayedIsSuccess,
        [styles.postOptInBanner]: displayedIsSuccess
      })}
      role="status"
      aria-live="polite"
      aria-atomic="true"
    >
      <div className={styles.banner}>
        <BannerIcon displayedIsSuccess={displayedIsSuccess} />
        <BannerCopy
          displayedIsSuccess={displayedIsSuccess}
          onNotifyMe={onNotifyMe}
        />
        {!displayedIsSuccess && (
          <button
            type="button"
            className={classnames(styles.notifyMeButton, styles.hideOnMobile)}
            onClick={onNotifyMe}
          >
            {content.saved_items_stock_marketing_opt_in_prompt_button}
          </button>
        )}
        {render && render()}
      </div>
    </div>
  );
};

NotificationsOptInBanner.propTypes = {
  render: PropTypes.func,
  className: PropTypes.string,
  onNotifyMe: PropTypes.func.isRequired,
  isSuccess: PropTypes.bool
};

export default NotificationsOptInBanner;
